/**
 * INFINI: compra rápida con selector de tallas en la tarjeta de producto (ver snippets/card-size-picker.liquid).
 *
 * - Elegir una talla envía el <form> del selector. Lo procesa <product-form> de Prestige: añade a la cesta por AJAX,
 *   actualiza la cesta y abre la cesta lateral (on_success = force_open_drawer).
 * - Cuando el cliente cambia de color en la tarjeta, se muestra el grupo de tallas de ese color.
 * - En móvil (sin hover) el panel se abre y cierra con el botón +. En escritorio aparece al pasar el ratón (CSS).
 */
class CardSizePicker extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.form = this.querySelector('form');
    this.toggleButton = this.querySelector('.card-size-picker__toggle');
    this.errorElement = this.querySelector('.card-size-picker__error');
    this.card = this.closest('product-card');

    this.form?.addEventListener('change', this.#onSizeSelected.bind(this), { signal });
    this.form?.addEventListener('variant:add', this.#onAdded.bind(this), { signal });
    this.form?.addEventListener('cart:error', this.#onError.bind(this), { signal });
    this.toggleButton?.addEventListener('click', this.#onToggle.bind(this), { signal });
    this.card?.addEventListener('change', this.#onCardColorChanged.bind(this), { signal });
    document.addEventListener('click', this.#onDocumentClick.bind(this), { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  get open() {
    return this.hasAttribute('open');
  }

  set open(value) {
    this.toggleAttribute('open', value);
    this.toggleButton?.setAttribute('aria-expanded', value ? 'true' : 'false');
  }

  #onToggle(event) {
    event.preventDefault();
    this.open = !this.open;
  }

  #onDocumentClick(event) {
    if (this.open && !this.contains(event.target)) {
      this.open = false;
    }
  }

  #onSizeSelected(event) {
    if (event.target.name !== 'id' || event.target.disabled) {
      return;
    }

    this.errorElement.hidden = true;
    this.form.requestSubmit();
  }

  #onAdded() {
    // Se desmarca la talla para poder volver a añadir la misma y se cierra el panel (móvil)
    this.form.querySelectorAll('input[name="id"]:checked').forEach((input) => (input.checked = false));
    this.open = false;
  }

  #onError(event) {
    this.form.querySelectorAll('input[name="id"]:checked').forEach((input) => (input.checked = false));

    if (this.errorElement && event.detail?.error) {
      this.errorElement.textContent = event.detail.error;
      this.errorElement.hidden = false;
    }
  }

  /**
   * Las muestras de color de la tarjeta son radios con data-variant-id (variante de ese color). Se busca esa variante
   * entre las tallas para saber qué grupo de color mostrar.
   */
  #onCardColorChanged(event) {
    const swatch = event.target;

    if (this.contains(swatch) || !swatch.matches?.('.product-card__info [type="radio"]')) {
      return;
    }

    const variantId = swatch.getAttribute('data-variant-id');
    const option = variantId ? this.form.querySelector(`input[name="id"][value="${variantId}"]`) : null;
    const group = option?.closest('.card-size-picker__group');

    if (!group) {
      return;
    }

    this.form.querySelectorAll('.card-size-picker__group').forEach((item) => (item.hidden = item !== group));
    this.errorElement.hidden = true;
  }
}

if (!window.customElements.get('card-size-picker')) {
  window.customElements.define('card-size-picker', CardSizePicker);
}
